import { useState, useMemo, useCallback, useEffect } from 'react';
import I18n from '../components/I18n';
import { useLocale } from '../Locale';
import { useLocation } from 'react-router-dom';
import { trackPage } from '../adobeAnalytics';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import Slider from 'react-slick';
import FooterNav from '../components/FooterNav';
import BgVid from '../assets/videos/origami_bg.mp4';

import CustomPicker from '../components/CustomPicker';

import { view } from '@risingstack/react-easy-state';
import OrigamiSelectionStore from '../state/OrigamiSelectionStore';

import { messages, patterns, colors, foldedAssets, createShareUrl } from '../origamiConfig';
import { Button } from '../components/Link';
import AnalyticsContext from '../components/AnalyticsContext';

const Origami = () => {
  const locale = useLocale();
  const location = useLocation();

  useEffect(() => {
		trackPage(`${locale}|origami`, 'customizer');
	}, [locale]);

  const [isFlipped, flip] = useState(false);
  const [isOrigamiSent, sendOrigami] = useState(false);
  const isOver = false;

  const selectedColor = OrigamiSelectionStore.selectedColor;
  const selectedPattern = OrigamiSelectionStore.selectedPattern;
  const selectedMessage = OrigamiSelectionStore.selectedMessage;

  const handleFlip = useCallback(() => {
	  flip(v => !v);
  }, []);

  const selectedVideo = useMemo(() => {
    return foldedAssets.find((asset) => asset.color === selectedColor.id && asset.pattern === selectedPattern.id)?.video;
  }, [selectedColor.id, selectedPattern.id]);

  const selectedVideoPort = useMemo(() => {
    return foldedAssets.find((asset) => asset.color === selectedColor.id && asset.pattern === selectedPattern.id)?.videoPort;
  }, [selectedColor.id, selectedPattern.id]);
  

  var settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    afterChange(index) {
      OrigamiSelectionStore.updateMessage(messages[index]);
    },
  };

  function startSendingOrigami() {
    let origamiAnimation = document.querySelector('.origami-animation-selected.landscape');
    if(window.innerHeight > window.innerWidth){
      origamiAnimation = document.querySelector('.origami-animation-selected.portrait');
    }
    sendOrigami(true);
    origamiAnimation.load();
    window.scrollTo(0, 0);
    setTimeout(function () {
      origamiAnimation.play();
      origamiAnimation.onended = () => {
		    window.location.href = createShareUrl(locale, selectedColor, selectedPattern, selectedMessage);;
      };
    }, 1000);
  }


  return (
    <AnalyticsContext type="div" className={`container origami fullscreen lang-${locale}`} componentName="origami">
      <div className="bg-video">
        <video src={BgVid} playsInline muted autoPlay loop type="video/mp4" />
        <div className={isOver ? 'origami-animation' : isOrigamiSent ? 'origami-animation is-playing' : 'origami-animation'}>
          <video className="origami-animation-selected landscape" playsInline muted>
            <source src={selectedVideo} type="video/mp4" />
          </video>
          <video className="origami-animation-selected portrait" playsInline muted>
            <source src={selectedVideoPort} type="video/mp4" />
          </video>
          
        </div>
      </div>
      <AnalyticsContext type="div" className={`content lang-${locale}`} componentName="content">
        <div className="page-logo lg nissan white">NISSAN</div>
        <div className={isOver ? 'origami-content sending over' : isOrigamiSent ? 'origami-content sending' : 'origami-content'}>
          <div className={isFlipped ? 'origami-copy flipped' : 'origami-copy'}>
            <h1>
              <I18n t="origami.subheadline" />
            </h1>
            <h2>
              <I18n t="origami.headline" />
            </h2>
            <p>
              <I18n t="origami.body.line1" />
              <br /><br />
              <I18n t="origami.body.line2" />
              <br /><br />
              <I18n t="origami.body.line3" />
              <br /><br /><br />
            </p>
            <p><strong><I18n t="origami.selectMessage" /></strong></p>
            <br />
            <p><I18n t="origami.selectMessageDesc" /></p>
          </div>
          <div className={isFlipped === true ? 'origami-paper view-pattern' : 'origami-paper view-message'}>
            <div className="origami-paper-content">
              <div className="origami-paper-text-selection">
                <div className="origami-paper-text-selection-slides">
                  <div className="origami-paper-text-selection-slides-content">
                    <Slider {...settings}>
                      {messages.map((item) => (
                        <div key={item.id} className="origami-paper-text-selection-slides-content-single">
                          <div className="text-user">
                            <p dangerouslySetInnerHTML={{ __html: I18n.getTranslation(location, item.text) }}></p>
                          </div>
                          <div className="text-nissan">
                            <p dangerouslySetInnerHTML={{ __html: I18n.getTranslation(location, item.disclaimer) }}></p>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            </div>
            <div className={`origami-paper-pattern paper-${selectedColor.name} paper-${selectedPattern.name}`}>
              <div className={`origami-paper-pattern-image ${selectedPattern.image} lang-${locale}`}></div>
            </div>
          </div>
          <div className={isFlipped ? "origami-cta show-options" : "origami-cta"}>
            <div className={'origami-options'}>
              <div className="origami-options-single">
                <p>
                  <I18n t="origami.colorSelector" />
                </p>
                <div className="origami-options-single-carousel">
                  <CustomPicker selections={colors} className="colors" onUpdate={OrigamiSelectionStore.updateColor} />
                </div>
              </div>
              <div className="origami-options-single">
                <p>
                  <I18n t="origami.patternSelector" />
                </p>
                <div className="origami-options-single-carousel">
                  <CustomPicker selections={patterns} className="patterns" onUpdate={OrigamiSelectionStore.updatePattern} />
                </div>
              </div>
              <div className="origami-pattern-desc">
                <p>
                  <I18n t={selectedPattern.description} />
                </p>
              </div>
            </div>
            <div className="origami-cta-buttons">
			      <Button className="btn white" onClick={handleFlip} text="origami.flipCta" />
            {isFlipped ? (
			      <Button className="btn white" onClick={startSendingOrigami} text="origami.sendCta" />
            ) : (
              ''
            )}
            </div>
          </div>
        </div>
      </AnalyticsContext>
      <FooterNav currentPage={'origami'} prev={{ text: 'general.back', url: 'discover' }} />
    </AnalyticsContext>
  );
};

export default view(Origami);
